<template>
	<v-card>
		<v-card-title>
			<v-layout row wrap>
				<v-flex lg4 md6 sm6 xs12 class="pr-3">
					<v-text-field v-model="search" append-icon="search" label="Search" autofocus=""></v-text-field>
				</v-flex>
			</v-layout>
			<v-layout row wrap>
				<v-spacer></v-spacer>
				<v-btn color="primary" @click="onCreateClick">Add new category</v-btn>
			</v-layout>
		</v-card-title>
		<v-data-table disable-initial-sort :headers="headers" :items="categories" :pagination.sync="pagination" :rows-per-page-items="rowsPerPageItems" :search="search">
			<template slot="items" slot-scope="props">
				<tr>
					<td class="text-xs-left">{{ props.item.name }}</td>
					<td class="text-xs-left">{{ props.item.description }}</td>
					<td class="text-xs-left">{{ transformAssetNoPrefix(props.item.assetNoPrefix) }}</td>
					<td class="justify-center px-0">
						<v-menu offset-y>
							<template v-slot:activator="{ on }">
								<v-btn small icon flat v-on="on">
									<v-icon>mdi-dots-horizontal</v-icon>
								</v-btn>
							</template>
							<v-list>
								<v-list-tile v-for="(action, index) in getAvailableActions(props.item)" :key="index" @click="action.callback">
									<v-list-tile-action
										><v-icon>{{ action.icon }}</v-icon></v-list-tile-action
									>
									<v-list-tile-title>{{ action.title }}</v-list-tile-title>
								</v-list-tile>
							</v-list>
						</v-menu>
					</td>
				</tr>
			</template>
		</v-data-table>

		<v-dialog v-model="showCategoryEditor" max-width="500px" persistent>
			<asset-category-editor v-if="showCategoryEditor" ref="categoryEditor" :is-saving="isUpdatingConfig" :category="categoryForEditor" @onSave="onSaveCategory" @closeEditor="showCategoryEditor = false" />
		</v-dialog>

		<confirmation-dialog
			v-if="showConfirmDeleteDialog"
			:dialog="showConfirmDeleteDialog"
			:title="'Delete Category: ' + categoryBeingDeleted.name"
			text="Are you sure you want to delete this category?"
			:isConfirming="isDeleting"
			@onConfirm="onConfirmDelete"
			@onCancel="showConfirmDeleteDialog = false"
		>
			<div v-if="assetsForCategoryDeleting.length > 0">
				<v-alert v-if="assetsForCategoryDeleting.length > 0" :value="true" type="warning" outline class="mt-2 text-xs-left">
					You currently have <strong>{{ assetsForCategoryDeleting.length }}</strong> asset{{ assetsForCategoryDeleting.length > 1 ? 's' : '' }} assigned to this category.
					<span v-if="categories.length > 1">You can either re-assign to another category or remove.</span>
					<span v-else>This will remove the category from all associated assets.</span>
				</v-alert>

				<div v-if="categories.length > 1" class="mt-4 text-xs-left">
					<p>What would you like to do?</p>
					<v-flex class="mt-4">
						<v-btn-toggle v-model="deletingAction">
							<v-btn flat value="assign" mandatory>
								Re-assign
							</v-btn>
							<v-btn flat value="remove">
								Remove Category
							</v-btn>
						</v-btn-toggle>
					</v-flex>

					<v-select v-if="deletingAction === 'assign'" v-model="reassignCategoryId" class="mt-4" :items="categoriesForReassign" item-text="name" item-value="id" label="Select new category" />
				</div>
			</div>
		</confirmation-dialog>
	</v-card>
</template>

<script>
	import { mapActions, mapGetters } from 'vuex';
	import AssetCategoryEditor from '@components/assets/AssetCategoryEdtior.vue';

	export default {
		components: { AssetCategoryEditor },
		data() {
			return {
				headers: [
					{ text: 'Name', value: 'name' },
					{ text: 'Description', value: 'description' },
					{ text: 'Asset Prefix', value: 'assetNoPrefix' },
					{ text: '', value: 'actions', sortable: false }
				],
				search: null,
				categoryForEditor: null,
				showCategoryEditor: false,
				showConfirmDeleteDialog: false,
				isDeleting: false,
				deletingId: null,
				deletingAction: 'remove',
				reassignCategoryId: null,
				pagination: {
					sortBy: 'name',
					rowsPerPage: 12
				},
				rowsPerPageItems: [12, 30, 60, { text: '$vuetify.dataIterator.rowsPerPageAll', value: -1 }]
			};
		},
		computed: {
			...mapGetters({
				categories: 'assetConfiguration/categories',
				isUpdatingConfig: 'assetConfiguration/isSaving',
				getAssetsByCategory: 'assets/getAssetsByCategory'
			}),
			assetsForCategoryDeleting() {
				return this.getAssetsByCategory(this.deletingId);
			},
			categoriesForReassign() {
				return this.categories.filter(category => category.id !== this.deletingId);
			},
			categoryBeingDeleted() {
				return this.categories.find(category => category.id === this.deletingId);
			}
		},
		methods: {
			...mapActions({
				upsertCategory: 'assetConfiguration/upsertAssetCategory',
				deleteCategory: 'assetConfiguration/deleteCategory',
				bulkUpdateAssets: 'assets/updateBulk'
			}),
			onCreateClick() {
				this.showCategoryEditor = true;
				this.categoryForEditor = null;
			},
			onEditClick(item) {
				this.showCategoryEditor = true;
				this.categoryForEditor = item;
			},
			onDeleteClick(id) {
				this.showConfirmDeleteDialog = true;
				this.deletingId = id;
				this.reassignCategoryId = this.categoriesForReassign.length > 0 ? this.categoriesForReassign[0].id : null;
			},
			async onSaveCategory(category) {
				const result = await this.upsertCategory(category);

				if (result.isSuccess) {
					this.showCategoryEditor = false;
				}
			},
			async onConfirmDelete() {
				this.isDeleting = true;

				if (this.assetsForCategoryDeleting.length > 0) {
					const bulkUpdateDto = this.assetsForCategoryDeleting.map(asset => {
						return {
							id: asset.id,
							categoryId: this.deletingAction === 'assign' ? this.reassignCategoryId : null,
							currentLocationId: asset.currentLocationId,
							status: asset.status
						};
					});

					const updateRes = await this.bulkUpdateAssets(bulkUpdateDto);
					if (!updateRes.isSuccess) {
						this.isDeleting = false;
						return;
					}
				}

				const result = await this.deleteCategory(this.deletingId);
				if (result.isSuccess) {
					this.deletingId = null;
					this.showConfirmDeleteDialog = false;
				}

				this.isDeleting = false;
			},
			getAvailableActions(item) {
				return [
					{
						title: 'Edit',
						icon: 'edit',
						callback: () => this.onEditClick(item)
					},
					{
						title: 'Delete',
						icon: 'delete',
						callback: () => this.onDeleteClick(item.id)
					}
				];
			},
			transformAssetNoPrefix(prefix) {
				if (!prefix) {
					return '';
				}
				// Check if the prefix ends with a hyphen
				if (!prefix.endsWith('-')) {
					// If not, add a hyphen to the end
					prefix += '-';
				}
				return prefix;
			}
		}
	};
</script>
